import { Component } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Catches unexpected render/runtime errors in the wrapped subtree so a single
 * broken component (e.g. a map/gallery edge case) cannot blank out or "close" the
 * entire admin form / page. Shows a small, friendly Turkish fallback with a retry
 * action instead of a white screen.
 *
 * Usage: wrap risky, isolated sections (map picker, media uploader) as well as the
 * whole app shell, so failures are contained as close to their source as possible.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div
            data-testid="error-boundary-fallback"
            className="flex flex-col items-center gap-3 rounded-[14px] border border-[color:var(--line)] bg-[color:hsl(var(--secondary))] p-6 text-center"
          >
            <AlertTriangle className="h-6 w-6 text-[color:hsl(var(--destructive))]" />
            <p className="text-sm text-[color:var(--brand-charcoal)]">
              {this.props.message || "Bu bölüm yüklenirken bir sorun oluştu. Diğer alanlarınız etkilenmedi."}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="error-boundary-retry-button"
              onClick={this.handleReset}
              className="border-[color:var(--line)] gap-1.5"
            >
              <RefreshCcw className="h-3.5 w-3.5" /> Tekrar Dene
            </Button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
